import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { useAuth } from '../../Contexts/AuthContext';
import { useToast } from '../../Contexts/ToastContext';

const EmailIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
);

const LockIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const inputIconStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '18px',
    transform: 'translateY(-50%)',
    color: '#999',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
};

const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { showToast } = useToast();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const redirectTo = (location.state as { from?: string } | null)?.from || '/account';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim() || !password.trim()) {
            showToast('Please enter your email and password', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            await login(email.trim(), password);
            showToast('Welcome back!', 'success');
            navigate(redirectTo, { replace: true });
        } catch (error: any) {
            const message =
                error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password' || error?.code === 'auth/user-not-found'
                    ? 'Incorrect email or password.'
                    : error?.message || 'Failed to sign in. Please try again.';
            showToast(message, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <Header />

            <section className="bg-img1 txt-center p-lr-15 p-tb-92" style={{ backgroundImage: `url('asset/images/bg-11.png')` }}>
                <h2 className="ltext-105 cl0 txt-center">Sign In</h2>
            </section>

            <section className="bg0 p-t-104 p-b-116">
                <div className="container">
                    <div className="flex-c-m">
                        <div className="size-210 bor10 p-lr-70 p-t-55 p-b-70 p-lr-15-lg w-full-md" style={{ maxWidth: 480 }}>
                            <form onSubmit={handleSubmit}>
                                <h4 className="mtext-105 cl2 txt-center p-b-30">
                                    Welcome Back
                                </h4>

                                <div className="bor8 m-b-20 how-pos4-parent">
                                    <input
                                        className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                        type="email"
                                        placeholder="Your Email Address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        autoComplete="email"
                                    />
                                    <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                        <EmailIcon />
                                    </span>
                                </div>

                                <div className="bor8 m-b-20 how-pos4-parent">
                                    <input
                                        className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                        type="password"
                                        placeholder="Your Password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        autoComplete="current-password"
                                    />
                                    <span className="how-pos4 pointer-none" style={inputIconStyle}>
                                        <LockIcon />
                                    </span>
                                </div>

                                <p className="stext-115 cl6 txt-right p-b-20">
                                    <Link to="/forgot-password" className="cl1" style={{ fontWeight: 600 }}>
                                        Forgot password?
                                    </Link>
                                </p>

                                <button
                                    type="submit"
                                    className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer w-full"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Signing In...' : 'Sign In'}
                                </button>

                                <p className="stext-115 cl6 txt-center p-t-30">
                                    Don't have an account?{' '}
                                    <Link to="/signup" className="cl1" style={{ fontWeight: 600 }}>
                                        Create one
                                    </Link>
                                </p>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Login;
